/**
 * Central media paths under /public (kebab-case folders, WebP rasters).
 * Use mediaPath() for encodeURI when rendering in src/href.
 */

export const mediaPath = (path) => (path ? encodeURI(path) : "");

export const BRAND = {
    logo: "/brand/girmitiya-logo.webp",
    logoAlt: "Global Girmitiya Council logo",
    favicon: "/favicon.ico",
};

export const HOME = {
    bannerHero: "/home/banner-hero.webp",
    bannerHeroAlt:
        "Historical photograph of Girmitiya indentured labourers and diaspora heritage",
    missionNeoBihar: "/home/mission-neo-bihar.webp",
    missionNeoBiharAlt:
        "Girmitiya community gathering representing cultural continuity in neo-Bihar",
};

export const ARCHIVES = {
    gallery: [
        { src: "/archives/gallery-01.webp", alt: "Girmitiya archival photograph, community records" },
        { src: "/archives/gallery-02.webp", alt: "Girmitiya archival photograph, plantation era" },
        { src: "/archives/gallery-03.webp", alt: "Girmitiya archival photograph, diaspora documents" },
        { src: "/archives/gallery-04.webp", alt: "Girmitiya archival photograph, historical ledger" },
    ],
};

export const ABOUT_GIRMITIYA = {
    meaningAndHistory: {
        src: "/about-girmitiya/meaning-and-history.webp",
        alt: "Illustration depicting the meaning and history of the term Girmitiya",
    },
    indentureSystem: {
        src: "/about-girmitiya/indenture-system.webp",
        alt: "Historical illustration of the colonial indenture system",
    },
    causesOfMigration: {
        src: "/about-girmitiya/causes-of-migration.webp",
        alt: "Illustration of causes of migration from India under indenture",
    },
    culturalImpact: {
        src: "/about-girmitiya/cultural-impact.webp",
        alt: "Illustration of social, economic, and cultural impact of Girmitiya migration",
    },
    girmitiyaVsSlavery: {
        src: "/about-girmitiya/girmitiya-vs-slavery.webp",
        alt: "Comparative illustration of Girmitiya indenture and slavery",
    },
};

export const EVENTS = {
    placeholder: "/events/placeholder.webp",
    placeholderAlt: "Girmitiya Council community event",
    g20: {
        screenshot: "/events/g20/welcome-screenshot.webp",
        screenshotAlt: "Girmitiya cultural welcome at the G-20 Summit in South Africa",
        video: "/events/g20/welcome-ceremony.mp4",
    },
    netherlands: {
        image01: "/events/netherlands/hague-cultural-01.webp",
        image02: "/events/netherlands/hague-cultural-02.webp",
        image03: "/events/netherlands/hague-cultural-03.webp",
        imageAlt: "Rajasthani folk dance at a Girmitiya cultural program in The Hague",
        video: "/events/netherlands/rajasthani-folk-dance.mp4",
        videoAlt: "/events/netherlands/rajasthani-folk-dance-alt.mp4",
    },
    seminars: {
        varanasi01: "/events/seminars/varanasi-01.webp",
        varanasi02: "/events/seminars/varanasi-02.webp",
        varanasi03: "/events/seminars/varanasi-03.webp",
        varanasiAlt:
            "International seminar on Pravasi Bharatiya and Vishwa Hindi Divas at Banaras Hindu University",
    },
    guyana: {
        video01: "/events/guyana/parliament-hindi-01.mp4",
        video02: "/events/guyana/parliament-hindi-02.mp4",
    },
};

export const COUNTRY_FIGURES = {
    mauritius: {
        src: "/countries/figures/mauritius/sir-seewoosagur-ramgoolam.webp",
        alt: "Portrait of Sir Seewoosagur Ramgoolam, statesman of Mauritius",
    },
    guyana: {
        src: "/countries/figures/guyana/chhedi.webp",
        alt: "Portrait of Girmitiya figure from British Guiana (Guyana)",
    },
    "trinidad-tobago": {
        src: "/countries/figures/trinidad/v-s-naipaul.webp",
        alt: "Portrait of V. S. Naipaul, writer from Trinidad",
    },
    jamaica: {
        src: "/countries/figures/jamaica/rajiv-maragh.webp",
        alt: "Portrait of Rajiv Maragh from Jamaica",
    },
    "saint-lucia": {
        src: "/countries/figures/saint-lucia/john-compton.webp",
        alt: "Portrait of John Compton, Prime Minister of Saint Lucia",
    },
    "saint-vincent": {
        src: "/countries/figures/saint-vincent/dr-arnold-thomas.webp",
        alt: "Portrait of Dr. Arnold Thomas from Saint Vincent",
    },
    fiji: {
        src: "/countries/figures/fiji/totaram-sanadhya.webp",
        alt: "Portrait of Totaram Sanadhya, Girmitiya writer and activist in Fiji",
    },
    malaysia: {
        src: "/countries/figures/malaya/janaky-athi-nahappan.webp",
        alt: "Portrait of Janaky Athi Nahappan, Malaysian independence activist",
    },
    "east-africa": {
        src: "/countries/figures/east-africa/makhan-singh.webp",
        alt: "Portrait of Makhan Singh, labour leader in East Africa",
    },
    seychelles: {
        src: "/countries/figures/seychelles/wavel-ramkalawan.webp",
        alt: "Portrait of Wavel Ramkalawan, President of Seychelles",
    },
    suriname: {
        src: "/countries/figures/suriname/santokhi.webp",
        alt: "Portrait of Chandrikapersad Santokhi, President of Suriname",
    },
    "south-africa": {
        src: "/countries/figures/south-africa/mohandas-gandhi.avif",
        alt: "Portrait of Mohandas Gandhi during his time in South Africa",
    },
};

/** @param {string} slug */
export const countryFigureMedia = (slug) => COUNTRY_FIGURES[slug] ?? null;
